import { Signer, providers } from "ethers";
import * as React from "react";
import { type HttpTransport } from "viem";
import {
 WalletClient,
 usePublicClient,
 useWalletClient,
 type PublicClient,
} from "wagmi";

export function publicClientToProvider(publicClient: PublicClient) {
 const { chain, transport } = publicClient;
 const network = {
  chainId: chain.id,
  name: chain.name,
  ensAddress: chain.contracts?.ensRegistry?.address,
 };
 if (transport.type === "fallback")
  return new providers.FallbackProvider(
   (transport.transports as ReturnType<HttpTransport>[]).map(
    ({ value }) => new providers.JsonRpcProvider(value?.url, network)
   )
  );
 return new providers.JsonRpcProvider(transport.url, network);
}

/** Hook to convert a viem Public Client to an ethers.js Provider. */
export function useEthersProvider({ chainId }: { chainId?: number } = {}) {
 const publicClient = usePublicClient({ chainId });
 return React.useMemo(
  () => publicClientToProvider(publicClient),
  [publicClient]
 );
}

export function walletClientToSigner(walletClient: WalletClient) {
 const { account, chain, transport } = walletClient;
 const network = {
  chainId: chain.id,
  name: chain.name,
  ensAddress: chain.contracts?.ensRegistry?.address,
 };
 const provider = new providers.Web3Provider(transport, network);
 const signer = provider.getSigner(account.address);
 return signer;
}

/** Hook to convert a viem Wallet Client to an ethers.js Signer. */
export function useEthersSigner({ chainId }: { chainId?: number } = {}) {
 const { data: walletClient } = useWalletClient({ chainId });
 return React.useMemo(
  () =>
   walletClient ? (walletClientToSigner(walletClient) as Signer) : undefined,
  [walletClient]
 );
}
